/**
 * Canvas Helpers
 * Helper library passed to animation scripts as the `helpers` parameter.
 * Provides color utilities, easing functions, noise, and fill helpers.
 *
 * Copied from app/imports/ui/services/lightshow/canvasHelpers.js
 * (no Meteor dependencies — identical logic)
 */

// --- Simplex 2D Noise (inline) ---
const F2 = 0.5 * (Math.sqrt(3) - 1);
const G2 = (3 - Math.sqrt(3)) / 6;
const grad3 = [
  [1, 1], [-1, 1], [1, -1], [-1, -1],
  [1, 0], [-1, 0], [0, 1], [0, -1],
];

const perm = new Uint8Array(512);
const permMod8 = new Uint8Array(512);
(() => {
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  let seed = 42;
  for (let i = 255; i > 0; i--) {
    seed = (seed * 16807 + 0) % 2147483647;
    const j = seed % (i + 1);
    [p[i], p[j]] = [p[j], p[i]];
  }
  for (let i = 0; i < 512; i++) {
    perm[i] = p[i & 255];
    permMod8[i] = perm[i] % 8;
  }
})();

function simplex2D(xin, yin) {
  const s = (xin + yin) * F2;
  const i = Math.floor(xin + s);
  const j = Math.floor(yin + s);
  const t = (i + j) * G2;
  const x0 = xin - (i - t);
  const y0 = yin - (j - t);

  const i1 = x0 > y0 ? 1 : 0;
  const j1 = x0 > y0 ? 0 : 1;

  const x1 = x0 - i1 + G2;
  const y1 = y0 - j1 + G2;
  const x2 = x0 - 1 + 2 * G2;
  const y2 = y0 - 1 + 2 * G2;

  const ii = i & 255;
  const jj = j & 255;

  let n0 = 0;
  let n1 = 0;
  let n2 = 0;

  let t0 = 0.5 - x0 * x0 - y0 * y0;
  if (t0 >= 0) {
    const gi0 = permMod8[ii + perm[jj]];
    t0 *= t0;
    n0 = t0 * t0 * (grad3[gi0][0] * x0 + grad3[gi0][1] * y0);
  }

  let t1 = 0.5 - x1 * x1 - y1 * y1;
  if (t1 >= 0) {
    const gi1 = permMod8[ii + i1 + perm[jj + j1]];
    t1 *= t1;
    n1 = t1 * t1 * (grad3[gi1][0] * x1 + grad3[gi1][1] * y1);
  }

  let t2 = 0.5 - x2 * x2 - y2 * y2;
  if (t2 >= 0) {
    const gi2 = permMod8[ii + 1 + perm[jj + 1]];
    t2 *= t2;
    n2 = t2 * t2 * (grad3[gi2][0] * x2 + grad3[gi2][1] * y2);
  }

  return 70 * (n0 + n1 + n2);
}

// --- Color utilities ---
const color = {
  hsl: (h, s, l) => `hsl(${h}, ${s}%, ${l}%)`,
  hsla: (h, s, l, a) => `hsla(${h}, ${s}%, ${l}%, ${a})`,
  rgb: (r, g, b) => `rgb(${r}, ${g}, ${b})`,
  rgba: (r, g, b, a) => `rgba(${r}, ${g}, ${b}, ${a})`,
  lerp: (colorA, colorB, t) => {
    const parseHex = (hex) => {
      const h = hex.replace('#', '');
      return [
        parseInt(h.substring(0, 2), 16),
        parseInt(h.substring(2, 4), 16),
        parseInt(h.substring(4, 6), 16),
      ];
    };
    const [r1, g1, b1] = parseHex(colorA);
    const [r2, g2, b2] = parseHex(colorB);
    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);
    return `rgb(${r}, ${g}, ${b})`;
  },
};

// --- Easing functions ---
const easing = {
  linear: (t) => t,
  easeIn: (t) => t * t,
  easeOut: (t) => t * (2 - t),
  easeInOut: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  sine: (t) => (1 - Math.cos(t * Math.PI)) / 2,
};

// --- Noise ---
const noise = {
  simplex2D,
};

// --- Stable hash for deterministic pseudo-random per cell index ---
// Uses Math.imul for correct 32-bit integer multiplication (plain * loses
// precision for large operands in float64, biasing the low bits).
function cellHash(i) {
  let x = Math.imul(i + 1, 0x9E3779B1) >>> 0;
  x = Math.imul((x >> 16) ^ x, 0x45d9f3b) >>> 0;
  return (x & 0xffff) / 0x10000;
}

// --- Fill helpers ---
const fill = {
  solid: (ctx, width, height, fillColor) => {
    ctx.fillStyle = fillColor;
    ctx.fillRect(0, 0, width, height);
  },
  gradient: (ctx, width, height, color1, color2, angle = 0) => {
    const rad = (angle * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const cx = width / 2;
    const cy = height / 2;
    const len = Math.max(width, height);
    const grad = ctx.createLinearGradient(
      cx - cos * len,
      cy - sin * len,
      cx + cos * len,
      cy + sin * len
    );
    grad.addColorStop(0, color1);
    grad.addColorStop(1, color2);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);
  },
  radial: (ctx, width, height, innerColor, outerColor) => {
    const cx = width / 2;
    const cy = height / 2;
    const radius = Math.max(width, height) / 2;
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    grad.addColorStop(0, innerColor);
    grad.addColorStop(1, outerColor);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);
  },
  stadium: (ctx, width, height, fillColor, rows, cols, participation, bgColor = '#111111') => {
    const cellW = width / cols;
    const cellH = height / rows;
    const radius = Math.max(1, Math.min(cellW, cellH) * 0.175);

    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, width, height);

    ctx.fillStyle = fillColor;
    ctx.beginPath();
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        if (cellHash(row * cols + col) < participation) {
          const cx = col * cellW + cellW / 2;
          const cy = row * cellH + cellH / 2;
          ctx.moveTo(cx + radius, cy);
          ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        }
      }
    }
    ctx.fill();
  },
};

// --- Zone rotation mapping ---
// Maps cardinal direction zone names to rotation degrees.
// North (default) = 0°, East = 90°, South = 180°, West = -90°.
// First rows always face the center of the venue.
const ZONE_ROTATION_MAP = {
  north: 0,
  east: 90,
  south: 180,
  west: -90,
};

function getZoneRotation(zoneName) {
  if (!zoneName) return 0;
  return ZONE_ROTATION_MAP[zoneName.toLowerCase()] ?? 0;
}

/**
 * Build the helpers object for a specific canvas and zone configuration
 */
export function buildHelpers(canvas, zoneIndex, zoneCount, params = {}, rowIndex = null, zoneOptions = []) {
  const zoneName = (zoneIndex != null && zoneOptions[zoneIndex]) || null;
  const zoneRotation = getZoneRotation(zoneName);
  const rotationRad = (zoneRotation * Math.PI) / 180;
  const w = canvas.width;
  const h = canvas.height;
  const isOrthogonal = zoneRotation === 90 || zoneRotation === -90;

  return {
    width: canvas.width,
    height: canvas.height,
    zoneIndex: zoneIndex ?? null,
    zoneCount: zoneCount || 0,
    rowIndex: rowIndex ?? null,
    zoneName,
    zoneRotation,
    params,
    color,
    easing,
    noise,
    fill,
    applyZoneRotation: (ctx) => {
      ctx.save();
      ctx.translate(w / 2, h / 2);
      ctx.rotate(rotationRad);
      if (isOrthogonal) {
        ctx.translate(-h / 2, -w / 2);
      } else {
        ctx.translate(-w / 2, -h / 2);
      }
    },
    restoreZoneRotation: (ctx) => {
      ctx.restore();
    },
  };
}
